import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { BannerType, BannerView } from "@dotnet/telematics-design-system";

import DialogActions from "components/Actions/dialogs/components/DialogActions";
import Dialog from "components/DesignComponents/Dialog";
import Tag, { TAG_COLORS, TAG_SIZES } from "components/DesignComponents/Tag";
import { useGetSingleTaskGroupEndpoint } from "components/TaskGroups/hooks";
import AlertContext from "context/alert/alertContext";
import {
    BackgroundActionData,
    ENDPOINTS,
    FILTER_NAMES,
    maxDialogWidth,
    TABLE_NAMES,
    TRI_SOURCES,
    useApi,
    useMediaQueries
} from "shared";

import { ACTIONS, ActionsFromButtonProps } from "./types";

// Cancel all cancelable tasks or Retry failed tasks
const ActionsFromButton = ({
    action,
    taskGroupId,
    data,
    isOpen,
    updateSingleRowData,
    close,
    closeMenu
}: ActionsFromButtonProps) => {
    const { removeAlertForBGAction, setBackgroundActionId } =
        useContext(AlertContext);

    const { t } = useTranslation();
    const { toMd, fromMd } = useMediaQueries();
    const { bulkCancel, postData, handleResponse } = useApi();

    const { getSingleTaskGroupData } = useGetSingleTaskGroupEndpoint();

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        closeMenu && isOpen && closeMenu();
    }, [isOpen, closeMenu]);

    const isCancelAction = action === ACTIONS.CancelAllCancelableTasks;

    const successCallback = (data: BackgroundActionData) => {
        setBackgroundActionId(
            data,
            TABLE_NAMES.Tasks,
            () => {
                close();
                getSingleTaskGroupData(taskGroupId, updateSingleRowData);
            },
            () => setIsLoading(false)
        );
    };

    const handleSubmit = async () => {
        removeAlertForBGAction();

        try {
            setIsLoading(true);

            if (isCancelAction) {
                const payload = {
                    source: TRI_SOURCES.Filtered,
                    [TRI_SOURCES.Filtered]: [
                        { [FILTER_NAMES.BatchId]: [String(taskGroupId)] }
                    ]
                };

                const { data }: { data: BackgroundActionData } =
                    await bulkCancel(ENDPOINTS.Tasks, payload);

                successCallback(data);
            } else {
                const { data }: { data: BackgroundActionData } = await postData(
                    `${ENDPOINTS.Batches}/${taskGroupId}/retryFailedTasks`
                );

                successCallback(data);
            }
        } catch (error) {
            handleResponse(error);
            setIsLoading(false);
        }
    };

    const title = isCancelAction
        ? t("Dialog##cancel tasks in group##title")
        : t("Dialog##retry tasks in group##title");

    const description = isCancelAction
        ? t("Dialog##cancel tasks in group##description")
        : t("Dialog##retry tasks in group##description");

    const loaderText = isCancelAction
        ? t("Button##cancelling")
        : t("Button##retrying");

    return (
        <Dialog
            isTitleSeparator
            isActionsSeparator
            title={title}
            description={description}
            open={isOpen}
            close={isLoading ? undefined : close}
            submit={handleSubmit}
            actions={
                <DialogActions
                    onClose={close}
                    isLoading={isLoading}
                    loaderText={loaderText}
                    isReadyToConfirm={true}
                    confirmationButtonText={t("Button##accept")}
                />
            }
            slotProps={{
                transition: {
                    onExited: () => {
                        removeAlertForBGAction();
                        setIsLoading(false);
                    }
                }
            }}
            extendToMaxWidth={toMd}
            css={css(fromMd && maxDialogWidth)}
        >
            <div
                css={css({
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    margin: "8px 0 16px 0"
                })}
            >
                {data.map(({ status_id, count }) => {
                    const title = `${t(
                        `Task status##task count##${status_id}`
                    )}: ${count}`;

                    return (
                        <Tag
                            key={status_id}
                            size={TAG_SIZES.Medium}
                            color={TAG_COLORS.Primary}
                            title={title}
                        />
                    );
                })}
            </div>

            {isCancelAction && (
                <BannerView
                    bannerType={BannerType.Warning}
                    title={t("Dialog##cancel tasks in group##banner title")}
                    css={css({
                        marginBottom: "16px"
                    })}
                />
            )}
        </Dialog>
    );
};

export default ActionsFromButton;
export { ACTIONS };
